import { SolidMeshObject } from "@/models/objects/solidMesh/SolidMeshObject";
import {
	SIMPLE_STRUCTURE_TYPE,
	type TSimpleStructureType,
} from "@/models/objects/solidMesh/types";
import { type DistanceUnit, distanceUnits } from "@/models/siteUnit.types";
import { createDistanceUnitPort } from "@/models/units/distanceUnitPort";
import {
	type PlainCartesian3 as TPoint3,
	buildBoxCulvertFromAnchor,
	buildCustomShapeFromAnchor,
	buildManholeFromAnchor,
	buildPipeFromTwoPoints,
	buildRetainingWallFromTwoPoints,
} from "earthbrain-viewer";
/**
 * useSimpleStructureCreation
 *
 * Singleton composable that owns the simple-structure creation workflow.
 * Reactive state is held in module-level refs (shared across all consumers).
 *
 * Vuex is only used for:
 *  - `solidMesh/setSelectedShapeType` (cross-component, read by ShapeTypeSwitchPanel)
 *  - `inProgressModeManager/*` (creation lock)
 *  - `object/addObject` (register new object)
 */
import { ref } from "vue";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Minimal store surface used by this composable (state + commit + dispatch). */
export interface TCreationStore {
	/** Only the site distance unit is read, to resolve the default dimensions. */
	state?: { site?: { units?: { distance?: DistanceUnit } } };
	commit(type: string, payload?: unknown, options?: { root?: boolean }): void;
	dispatch(
		type: string,
		payload?: unknown,
		options?: { root?: boolean },
	): Promise<unknown>;
}

export type TCreationPhase = "idle" | "selecting" | "picking" | "confirming";

export type TCreationNotification = {
	type: "success" | "error";
	key: string;
};

export type TSimpleStructureConfirmData = {
	structureType: TSimpleStructureType;
	name?: string;
	color?: { red?: number; green?: number; blue?: number; alpha?: number };
	viewerParams?: {
		dimensions: { x: number; y: number; z: number };
		center: { x: number; y: number; z: number };
		rotation?: { x?: number; y?: number; z?: number };
		meta?: { anchorType?: string; bottomZ?: number };
	};
};

// ---------------------------------------------------------------------------
// Module-level reactive state (singleton)
// ---------------------------------------------------------------------------

const creationPhase = ref<TCreationPhase>("idle");
const creationTempMeshId = ref<string | null>(null);
const creationPickedPosition = ref<TPoint3 | null>(null);
const creationTwoPointPositions = ref<{
	p1: TPoint3;
	p2: TPoint3;
} | null>(null);
const creationTwoPointStep = ref<0 | 1 | 2>(0);
const creationNotification = ref<TCreationNotification | null>(null);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resetCreationState(store: TCreationStore) {
	creationPhase.value = "idle";
	creationTempMeshId.value = null;
	creationPickedPosition.value = null;
	creationTwoPointPositions.value = null;
	creationTwoPointStep.value = 0;
	store.commit("solidMesh/setSelectedShapeType", "box");
}

/**
 * Default dimensions handed to the viewer builders, already in their boundary
 * units (mm / m). Feet sites use feet-specific values, not converted metric ones.
 */
export type TCreationDefaultDimensions = {
	pipeDiameterMm: number;
	boxCulvertWidthMm: number;
	boxCulvertHeightMm: number;
	boxCulvertLengthM: number;
	retainingWallHeightM: number;
	manholeDiameterMm: number;
	manholeDepthM: number;
	/** Cube edge / sphere diameter / cylinder diameter and height, in metres. */
	customShapeM: number;
};

/** Metric site defaults. Existing values, kept as-is. */
const METRIC_DEFAULT_DIMENSIONS: TCreationDefaultDimensions = {
	pipeDiameterMm: 500,
	boxCulvertWidthMm: 1000,
	boxCulvertHeightMm: 500,
	boxCulvertLengthM: 1,
	retainingWallHeightM: 3,
	manholeDiameterMm: 600,
	manholeDepthM: 1.5,
	customShapeM: 10,
};

/** Feet site defaults, expressed in feet. Converted to m / mm before use. */
const FEET_DEFAULT_DIMENSIONS_FT = {
	pipeDiameter: 1,
	boxCulvertWidth: 3,
	boxCulvertHeight: 2,
	boxCulvertLength: 3,
	retainingWallHeight: 3,
	manholeDiameter: 1,
	manholeDepth: 3,
	customShape: 30,
} as const;

/** Resolves the creation defaults for the site distance unit held in the store. */
export function resolveDefaultDimensions(
	store: TCreationStore,
): TCreationDefaultDimensions {
	const distanceUnit = store.state?.site?.units?.distance;
	if (distanceUnit === undefined || distanceUnit === distanceUnits.METERS) {
		return METRIC_DEFAULT_DIMENSIONS;
	}
	const port = createDistanceUnitPort(distanceUnit);
	const ft = FEET_DEFAULT_DIMENSIONS_FT;
	return {
		pipeDiameterMm: port.toMeters(ft.pipeDiameter) * 1000,
		boxCulvertWidthMm: port.toMeters(ft.boxCulvertWidth) * 1000,
		boxCulvertHeightMm: port.toMeters(ft.boxCulvertHeight) * 1000,
		boxCulvertLengthM: port.toMeters(ft.boxCulvertLength),
		retainingWallHeightM: port.toMeters(ft.retainingWallHeight),
		manholeDiameterMm: port.toMeters(ft.manholeDiameter) * 1000,
		manholeDepthM: port.toMeters(ft.manholeDepth),
		customShapeM: port.toMeters(ft.customShape),
	};
}

function safeDeleteMesh(meshId: string | null) {
	if (!meshId) return;
	try {
		window.viewer.deleteSolidMesh(meshId);
	} catch (e: unknown) {
		console.warn("[SolidMesh] _safeDeleteMesh failed:", e);
	}
}

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------

export function useSimpleStructureCreation(store: TCreationStore) {
	// ----- Actions -----

	function startSimpleStructureCreation() {
		resetCreationState(store);
		creationPhase.value = "selecting";
	}

	async function cancelSimpleStructureCreation() {
		creationPhase.value = "idle";
		try {
			window.viewer.cancelSolidMeshConstruction();
		} catch (e: unknown) {
			console.warn("[SolidMesh] cancelSolidMeshConstruction failed:", e);
		}
		safeDeleteMesh(creationTempMeshId.value);
		resetCreationState(store);
		try {
			await store.dispatch("inProgressModeManager/cancelCreation", null, {
				root: true,
			});
		} catch (e: unknown) {
			console.warn("[SolidMesh] cancelCreation dispatch failed:", e);
		}
	}

	function clearCreationNotification() {
		creationNotification.value = null;
	}

	async function selectSimpleStructureType(
		structureType: TSimpleStructureType,
	) {
		const defaults = resolveDefaultDimensions(store);
		store.commit("solidMesh/setSelectedShapeType", structureType);
		creationPhase.value = "picking";
		await store.dispatch(
			"inProgressModeManager/startCreation",
			{ objectType: "SolidMesh" },
			{ root: true },
		);

		const shapeTypeMap: Record<string, string> = {
			[SIMPLE_STRUCTURE_TYPE.BOX_CULVERT]: "box",
			[SIMPLE_STRUCTURE_TYPE.RETAINING_WALL]: "box",
			[SIMPLE_STRUCTURE_TYPE.MANHOLE]: "cylinder",
			[SIMPLE_STRUCTURE_TYPE.PIPE]: "cylinder",
		};
		const shapeType = shapeTypeMap[structureType] ?? "box";

		// ----- Two-point structures -----
		if (
			structureType === SIMPLE_STRUCTURE_TYPE.PIPE ||
			structureType === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL
		) {
			creationTwoPointStep.value = 1;

			const twoPointAnchorMap: Record<string, string> = {
				[SIMPLE_STRUCTURE_TYPE.PIPE]: "bottom-start",
				[SIMPLE_STRUCTURE_TYPE.RETAINING_WALL]: "top-start",
			};
			const twoPointAnchorType = twoPointAnchorMap[structureType] ?? null;

			const twoPointFactory = ([pt1, pt2]: [TPoint3, TPoint3]) => {
				if (structureType === SIMPLE_STRUCTURE_TYPE.PIPE) {
					const r = buildPipeFromTwoPoints({
						P1: pt1,
						P2: pt2,
						diameter_mm: defaults.pipeDiameterMm,
					});
					return {
						shapeType: "cylinder" as const,
						localCenter: r.center,
						dimensions: r.dimensions,
						hpr: {
							heading: r.rotation.z,
							pitch: r.rotation.y,
							roll: r.rotation.x,
						},
					};
				}
				// Retaining wall: height adds to picked Z so the wall
				// grows upward from the terrain surface.
				const defaultHeight = defaults.retainingWallHeightM;
				const r = buildRetainingWallFromTwoPoints({
					P1: { ...pt1, z: pt1.z + defaultHeight },
					P2: { ...pt2, z: pt2.z + defaultHeight },
					height_m: defaultHeight,
					gradient_percent: 0,
				});
				return {
					shapeType: "box" as const,
					localCenter: r.center,
					dimensions: r.dimensions,
					hpr: {
						heading: r.rotation.z,
						pitch: r.rotation.y,
						roll: r.rotation.x,
					},
					attributes: {
						bottomZ: r.meta.bottomZ,
					},
				};
			};

			try {
				window.viewer.startMultiPointConstruction({
					meta: { anchorType: twoPointAnchorType },
					shapeType,
					pointCount: 2,
					shapeFactory: twoPointFactory,
					onStart: () => {},
					onProgress: (data) => {
						if (data?.isUndo) {
							creationTwoPointStep.value = 1;
						} else if (data?.step === 0) {
							creationTwoPointStep.value = 2;
						}
					},
					onFinish: async (solidMeshIds, extra) => {
						const meshId = solidMeshIds?.[0];
						const positions = extra?.positions ?? [];
						try {
							const p1 = positions[0] ?? { x: 0, y: 0, z: 0 };
							const p2 = positions[1] ?? { x: 0, y: 0, z: 0 };
							creationTwoPointPositions.value = { p1, p2 };
							creationTempMeshId.value = meshId ?? null;
							creationTwoPointStep.value = 0;

							if (meshId && twoPointAnchorType) {
								try {
									await window.viewer.setSolidMeshAnchorType(
										meshId,
										twoPointAnchorType,
										{ preservePosition: true },
									);
								} catch (err: unknown) {
									console.warn(
										"Failed to set anchor type for temp two-point mesh:",
										err,
									);
								}
							}
							creationPhase.value = "confirming";
						} catch (error: unknown) {
							console.error("Failed to process two-point pick result:", error);
							safeDeleteMesh(meshId);
							resetCreationState(store);
							creationNotification.value = {
								type: "error",
								key: "COORDINATE_ACQUISITION_FAILED",
							};
							await store.dispatch(
								"inProgressModeManager/cancelCreation",
								null,
								{ root: true },
							);
						}
					},
					onCancel: async () => {
						resetCreationState(store);
						await store.dispatch("inProgressModeManager/cancelCreation", null, {
							root: true,
						});
					},
					onError: async (error) => {
						console.error("Simple structure two-point picker error:", error);
						resetCreationState(store);
						creationNotification.value = {
							type: "error",
							key: "COORDINATE_ACQUISITION_FAILED",
						};
						await store.dispatch("inProgressModeManager/cancelCreation", null, {
							root: true,
						});
					},
				});
			} catch (e: unknown) {
				resetCreationState(store);
				await store.dispatch("inProgressModeManager/cancelCreation", null, {
					root: true,
				});
				console.error("Error starting two-point picker:", e);
			}
			return;
		}

		// ----- Single-point structures -----
		const singlePointFactory = ([anchor]: [TPoint3]) => {
			if (structureType === SIMPLE_STRUCTURE_TYPE.BOX_CULVERT) {
				const r = buildBoxCulvertFromAnchor({
					P: anchor,
					width_mm: defaults.boxCulvertWidthMm,
					height_mm: defaults.boxCulvertHeightMm,
					length_m: defaults.boxCulvertLengthM,
				});
				return {
					shapeType: "box" as const,
					localCenter: r.center,
					dimensions: r.dimensions,
				};
			}
			const r = buildManholeFromAnchor({
				P: anchor,
				diameter_mm: defaults.manholeDiameterMm,
				depth_m: defaults.manholeDepthM,
			});
			return {
				shapeType: "cylinder" as const,
				localCenter: r.center,
				dimensions: r.dimensions,
			};
		};

		const singleAnchorTypeMap: Record<string, string> = {
			[SIMPLE_STRUCTURE_TYPE.BOX_CULVERT]: "edge-midpoint",
			[SIMPLE_STRUCTURE_TYPE.MANHOLE]: "top-center",
		};

		try {
			window.viewer.startSolidMeshConstruction({
				shapeType,
				meta: { anchorType: singleAnchorTypeMap[structureType] ?? null },
				shapeFactory: singlePointFactory,
				onFinish: async (solidMeshIds: string[]) => {
					if (solidMeshIds && solidMeshIds.length > 0) {
						const solidMeshId = solidMeshIds[0];
						try {
							const anchor =
								await window.viewer.getSolidMeshAnchorLocal(solidMeshId);
							creationPickedPosition.value = {
								x: anchor.x,
								y: anchor.y,
								z: anchor.z,
							};
							creationTempMeshId.value = solidMeshId;
							creationPhase.value = "confirming";
						} catch (error: unknown) {
							console.error("Failed to get anchor coordinates:", error);
							safeDeleteMesh(solidMeshId);
							resetCreationState(store);
							creationNotification.value = {
								type: "error",
								key: "COORDINATE_ACQUISITION_FAILED",
							};
							await store.dispatch(
								"inProgressModeManager/cancelCreation",
								null,
								{ root: true },
							);
						}
					}
				},
				onStart: () => {},
				onProgress: () => {},
				onCancel: async () => {
					resetCreationState(store);
					await store.dispatch("inProgressModeManager/cancelCreation", null, {
						root: true,
					});
				},
				onError: async (error) => {
					console.error("Simple structure picker error:", error);
					safeDeleteMesh(creationTempMeshId.value);
					resetCreationState(store);
					creationNotification.value = {
						type: "error",
						key: "COORDINATE_ACQUISITION_FAILED",
					};
					await store.dispatch("inProgressModeManager/cancelCreation", null, {
						root: true,
					});
				},
			});
		} catch (e: unknown) {
			resetCreationState(store);
			await store.dispatch("inProgressModeManager/cancelCreation", null, {
				root: true,
			});
			console.error("Error starting single-point picker:", e);
		}
	}

	async function confirmSimpleStructureParams(
		data: TSimpleStructureConfirmData,
	) {
		creationPhase.value = "idle";
		const tempMeshId = creationTempMeshId.value;

		try {
			window.viewer.cancelSolidMeshConstruction();
		} catch (e: unknown) {
			console.warn("[SolidMesh] cancelSolidMeshConstruction failed:", e);
		}

		try {
			if (!tempMeshId) throw new Error("No temporary mesh ID found");

			const anchorTypeMap: Record<string, string> = {
				[SIMPLE_STRUCTURE_TYPE.BOX_CULVERT]: "edge-midpoint",
				[SIMPLE_STRUCTURE_TYPE.MANHOLE]: "top-center",
				[SIMPLE_STRUCTURE_TYPE.PIPE]: "bottom-start",
				[SIMPLE_STRUCTURE_TYPE.RETAINING_WALL]: "top-start",
			};
			const vp = data.viewerParams;
			const anchorType =
				vp?.meta?.anchorType ?? anchorTypeMap[data.structureType];

			if (!vp || !anchorType) {
				throw new Error(
					`Missing viewerParams or anchorType for ${data.structureType}`,
				);
			}

			await window.viewer.setSolidMeshAnchorType(tempMeshId, anchorType, {
				preservePosition: true,
			});
			await window.viewer.applyPreviewParams(tempMeshId, {
				dimensions: vp.dimensions,
				localCenter: vp.center,
				hprRad: {
					heading: vp.rotation?.z ?? 0,
					pitch: vp.rotation?.y ?? 0,
					roll: vp.rotation?.x ?? 0,
				},
			});
			if (vp.meta?.bottomZ != null) {
				window.viewer.updateSolidMeshAttributes(tempMeshId, {
					bottomZ: vp.meta.bottomZ,
				});
			}

			const editCallback = {
				onFinish: async () => {
					try {
						const solidMesh = SolidMeshObject.createFromViewer(
							window.viewer,
							tempMeshId,
							{
								structureType: data.structureType,
							},
						);
						if (solidMesh) {
							solidMesh.name = data.name || solidMesh.name;
							const colorArr = [
								data.color?.red ?? 0.5,
								data.color?.green ?? 0.5,
								data.color?.blue ?? 0.5,
							];
							await window.viewer.setSolidMeshColor(tempMeshId, colorArr);
							await window.viewer.setSolidMeshTransparency(
								tempMeshId,
								data.color?.alpha ?? 1,
							);
							await solidMesh.setVisibility(true);
							try {
								await solidMesh.endEdit();
								store.commit("object/addObject", solidMesh, { root: true });
								resetCreationState(store);
								creationNotification.value = {
									type: "success",
									key: "ADD_SIMPLE_STRUCTURE",
								};
							} catch (saveErr: unknown) {
								console.error("[SimpleStructure] endEdit() error", saveErr);
								creationNotification.value = {
									type: "error",
									key: "ADD_SIMPLE_STRUCTURE",
								};
								resetCreationState(store);
							}
						}
					} catch (error: unknown) {
						console.error("Failed to create solid mesh object:", error);
						creationNotification.value = {
							type: "error",
							key: "ADD_SIMPLE_STRUCTURE",
						};
						resetCreationState(store);
					}
				},
				onStart: () => {},
				onProgress: () => {},
				onCancel: () => {
					safeDeleteMesh(tempMeshId);
					resetCreationState(store);
				},
				onError: (error: unknown) => {
					console.error("Edit mode error:", error);
					safeDeleteMesh(tempMeshId);
					resetCreationState(store);
				},
			};

			await window.viewer.startSolidMeshEdit(tempMeshId, editCallback);
			await window.viewer.finishSolidMeshEdit();
			await store.dispatch("inProgressModeManager/cancelCreation", null, {
				root: true,
			});
		} catch (e: unknown) {
			console.error("Error creating simple structure:", e);
			creationNotification.value = {
				type: "error",
				key: "ADD_SIMPLE_STRUCTURE",
			};
			safeDeleteMesh(tempMeshId);
			resetCreationState(store);
			await store.dispatch("inProgressModeManager/cancelCreation", null, {
				root: true,
			});
		}
	}

	async function startManualSolidMeshCreation() {
		const defaults = resolveDefaultDimensions(store);
		resetCreationState(store);
		store.commit("solidMesh/setSelectedShapeType", "box");
		await store.dispatch(
			"inProgressModeManager/startCreation",
			{ objectType: "SolidMesh" },
			{ root: true },
		);

		try {
			window.viewer.startSolidMeshConstruction({
				shapeType: "box",
				// Custom shapes follow whichever box / sphere / cylinder the handler
				// currently holds; the factory only sizes it with the site-unit default.
				shapeFactory: (positions, ctx) => {
					const d = defaults.customShapeM;
					const r = buildCustomShapeFromAnchor({
						P: positions[0],
						dimensions: { x: d, y: d, z: d },
					});
					return {
						shapeType: ctx.shapeType,
						localCenter: r.center,
						dimensions: r.dimensions,
					};
				},
				onFinish: async (solidMeshIds: string[]) => {
					try {
						if (solidMeshIds && solidMeshIds.length > 0) {
							const solidMeshId = solidMeshIds[0];
							try {
								creationTempMeshId.value = solidMeshId;
								window.viewer.setSolidMeshVisibility(solidMeshId, true);
								store.commit("solidMesh/setSelectedShapeType", "custom");
								const event = new CustomEvent("openPropertyDialog", {
									detail: { id: solidMeshId, type: 13 },
								});
								window.dispatchEvent(event);
							} catch (error: unknown) {
								console.error("Failed to create solid mesh:", error);
								safeDeleteMesh(solidMeshId);
								creationNotification.value = {
									type: "error",
									key: "ADD_SIMPLE_STRUCTURE",
								};
							}
						}
					} finally {
						await store.dispatch("inProgressModeManager/cancelCreation", null, {
							root: true,
						});
					}
				},
				onStart: () => {},
				onProgress: () => {},
				onCancel: async () => {
					await store.dispatch("inProgressModeManager/cancelCreation", null, {
						root: true,
					});
				},
				onError: async (error: unknown) => {
					console.error("Solid mesh construction error:", error);
					creationNotification.value = {
						type: "error",
						key: "ADD_SIMPLE_STRUCTURE",
					};
					await cancelManualSolidMeshCreation();
				},
			});
		} catch (e: unknown) {
			console.error("Error on manual SolidMesh creation:", e);
			creationNotification.value = {
				type: "error",
				key: "ADD_SIMPLE_STRUCTURE",
			};
			await store.dispatch("inProgressModeManager/cancelCreation", null, {
				root: true,
			});
		}
	}

	async function cancelManualSolidMeshCreation() {
		try {
			window.viewer.cancelSolidMeshConstruction();
		} catch (e: unknown) {
			console.warn("[SolidMesh] cancelSolidMeshConstruction failed:", e);
		}
		await store.dispatch("inProgressModeManager/cancelCreation", null, {
			root: true,
		});
	}

	return {
		// State (readonly for consumers)
		creationPhase,
		creationTempMeshId,
		creationPickedPosition,
		creationTwoPointPositions,
		creationTwoPointStep,
		creationNotification,
		// Actions
		startSimpleStructureCreation,
		cancelSimpleStructureCreation,
		selectSimpleStructureType,
		confirmSimpleStructureParams,
		clearCreationNotification,
		startManualSolidMeshCreation,
		cancelManualSolidMeshCreation,
	};
}
